import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

import { env } from '@/env';

const DEFAULT_APP_NAME = 'Dash Social';

/**
 * Composes the browser tab title from the primary-nav section and the active secondary-nav item.
 *
 * Two writers, one owner: the router `afterEach` supplies the section, each mounted
 * `SecondaryNavigationMenu` supplies its active item. The store is the only thing that touches
 * `document.title`, so neither writer can clobber the other's half.
 */
export const usePageTitleStore = defineStore('page-title', () => {
  const section = ref<string | null>(null);
  // Keyed per menu instance: Dashboards, Campaigns and Listening mount two menus at once, and
  // the one with no match must not erase the label the other one published.
  const secondaryLabelsBySource = ref<Record<string, string | null>>({});

  let nextSourceId = 0;

  const secondary = computed(
    () => Object.values(secondaryLabelsBySource.value).find(Boolean) ?? null,
  );

  const title = computed(() => {
    const appName = env.brandingAppName ?? DEFAULT_APP_NAME;
    const location = [section.value, secondary.value].filter(Boolean).join(' - ');
    return location ? `${location} · ${appName}` : appName;
  });

  // Imperative rather than a watcher so callers (and their tests) see the DOM update synchronously.
  function applyTitle() {
    document.title = title.value;
  }

  function setSection(label: string | null) {
    section.value = label;
    applyTitle();
  }

  /** Mint the key a secondary-nav menu publishes under; one per mounted menu, unique for the pinia. */
  function registerSecondarySource(): string {
    nextSourceId += 1;
    return `secondary-nav-${nextSourceId}`;
  }

  function setSecondaryLabel(sourceId: string, label: string | null) {
    secondaryLabelsBySource.value = { ...secondaryLabelsBySource.value, [sourceId]: label };
    applyTitle();
  }

  function clearSecondaryLabel(sourceId: string) {
    const { [sourceId]: _removed, ...rest } = secondaryLabelsBySource.value;
    secondaryLabelsBySource.value = rest;
    applyTitle();
  }

  return {
    section,
    secondary,
    title,
    setSection,
    registerSecondarySource,
    setSecondaryLabel,
    clearSecondaryLabel,
  };
});
